import { useState } from "react";
import { Activity, BatteryCharging, Gauge, TrendingUp, TriangleAlert, Zap } from "lucide-react";
import { AnimatedNumber } from "@/components/aion/AnimatedNumber";
import { Button } from "@/components/ui/button";

const numberOrNull = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

export const EnergyPanel = ({ status, devices, forecast, token, onOptimize }) => {
  const [optimizing, setOptimizing] = useState(false);
  const [error, setError] = useState("");
  const currentPower = numberOrNull(status?.current_power);
  const dailyKwh = numberOrNull(status?.daily_kwh);
  const trackedToday = numberOrNull(status?.tracked_today_kwh, status?.daily_kwh);
  const projectedCostDay = numberOrNull(status?.estimated_cost_day, forecast?.projected_cost_day);
  const nextDayKwh = numberOrNull(forecast?.next_day_kwh);
  const productionNow = numberOrNull(status?.current_production, status?.solar_generation_watts, status?.production_watts);
  const batteryLevel = numberOrNull(status?.battery_level, status?.battery_percent, status?.battery_soc);
  const valueToday = numberOrNull(status?.savings_today, status?.energy_value, status?.estimated_value_day);
  const risk = String(forecast?.anomaly_risk || "unknown").toLowerCase();
  const topDevices = (devices?.devices || []).slice(0, 8);

  const metrics = [
    { key: "energy-current-power", label: "Current load", value: currentPower, suffix: " W", decimals: 1, icon: Zap, testId: "energy-current-power" },
    { key: "energy-daily-kwh", label: "Consumption today", value: trackedToday, suffix: " kWh", decimals: 3, icon: Activity, testId: "energy-tracked-today" },
    { key: "energy-forecast-day", label: "Forecast / day", value: nextDayKwh, suffix: " kWh", decimals: 3, icon: TrendingUp, testId: "energy-forecast-day" },
    { key: "energy-cost-day", label: "Cost / day", value: projectedCostDay, prefix: "$", decimals: 2, icon: Gauge, testId: "energy-cost-day" },
    { key: "energy-daily-total", label: "24h estimate", value: dailyKwh, suffix: " kWh", decimals: 3, icon: Gauge, testId: "energy-daily-kwh" },
    { key: "energy-live-source", label: "Pipeline source", value: status?.energy_source || "live_runtime_pipeline", icon: BatteryCharging, testId: "energy-live-source", isText: true },
  ];

  if (productionNow !== null) metrics.splice(4, 0, { key: "energy-production-now", label: "Production", value: productionNow, suffix: " W", decimals: 1, icon: TrendingUp, testId: "energy-production-now" });
  if (batteryLevel !== null) metrics.splice(5, 0, { key: "energy-battery-level", label: "Battery", value: batteryLevel, suffix: "%", decimals: 0, icon: BatteryCharging, testId: "energy-battery-level" });
  if (valueToday !== null) metrics.push({ key: "energy-value-today", label: "Value", value: valueToday, prefix: "$", decimals: 2, icon: TrendingUp, testId: "energy-value-today" });

  const optimize = async () => {
    setOptimizing(true);
    setError("");
    try {
      await onOptimize?.(token);
    } catch (optimizeError) {
      setError(optimizeError.message || "Optimization failed");
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <section className="aion-section-card aion-energy-panel" data-testid="energy-panel" data-energy-risk={risk}>
      <div className="aion-energy-header">
        <div>
          <p className="aion-section-eyebrow">AION ENERGY INTELLIGENCE</p>
          <h2 className="aion-section-title" data-testid="energy-panel-title">Energie-System</h2>
          <p className="aion-section-subtitle">Live-Leistung, Verbrauch, Kosten und Gerätebedarf aus dem verbundenen Energiesystem.</p>
        </div>
        <div className="aion-energy-chip" data-testid="energy-panel-chip">
          <AnimatedNumber value={currentPower} decimals={1} suffix=" W" fallback="—" dataTestId="energy-panel-chip-value" />
        </div>
      </div>

      {error ? <div className="aion-error-box" data-testid="energy-panel-error">{error}</div> : null}

      <div className="aion-energy-spotlight" data-testid="energy-spotlight">
        <div className="aion-energy-spotlight-copy">
          <span className="aion-status-badge">Live-Verbrauch</span>
          <strong data-testid="energy-spotlight-power">
            <AnimatedNumber value={currentPower} decimals={1} suffix=" W" fallback="—" dataTestId="energy-spotlight-power-value" />
          </strong>
          <small data-testid="energy-spotlight-copy">Prognoserisiko: {forecast?.anomaly_risk || "unknown"}</small>
        </div>
        <div className="aion-energy-track" aria-hidden="true">
          <span className="aion-energy-track-bar aion-energy-track-bar-live" style={{ "--aion-energy-width": `${Math.max(8, Math.min(100, (currentPower || 0) / 2.4))}%` }} />
          <span className="aion-energy-track-bar aion-energy-track-bar-forecast" style={{ "--aion-energy-width": `${Math.max(8, Math.min(100, (nextDayKwh || 0) * 48))}%` }} />
        </div>
      </div>

      <div className="aion-energy-grid" data-testid="energy-status-grid">
        {metrics.map(({ key, label, value, suffix, prefix, decimals = 0, icon: Icon, testId, isText }) => (
          <div className="aion-energy-metric" key={key} data-testid={testId}>
            <div>
              <span>{label}</span>
              <strong>
                {isText ? value : <AnimatedNumber value={value} decimals={decimals} prefix={prefix} suffix={suffix} fallback="—" dataTestId={`${testId}-value`} />}
              </strong>
            </div>
            <Icon size={18} aria-hidden="true" />
          </div>
        ))}
      </div>

      <div className="aion-energy-action-row" data-testid="energy-optimize-row">
        <Button onClick={optimize} disabled={optimizing} data-testid="energy-optimize-button">
          {optimizing ? "Optimiert…" : "Energie optimieren"}
        </Button>
        <div className="aion-energy-risk-note" data-testid="energy-forecast-risk">
          <TriangleAlert size={16} aria-hidden="true" />
          Prognoserisiko: {forecast?.anomaly_risk || "unknown"}
        </div>
      </div>

      <div className="aion-energy-device-list" data-testid="energy-device-list">
        {topDevices.map((device) => (
          <div className="aion-energy-device-row" key={device.device_id} data-testid={`energy-device-${device.device_id}`}>
            <div>
              <strong>{device.device_name || device.device_id}</strong>
              <small>{device.room_name || "Raum"} · {device.state} · {device.estimated_kwh_day} kWh today · ${device.cost_per_hour}/h</small>
            </div>
            <div className="aion-energy-device-tail">
              <span data-testid={`energy-device-watts-${device.device_id}`}>
                <AnimatedNumber value={device.watts} decimals={1} suffix="W" fallback="—" dataTestId={`energy-device-watts-value-${device.device_id}`} />
              </span>
              <span className="aion-energy-device-meter" aria-hidden="true">
                <span className="aion-energy-device-meter-fill" style={{ "--aion-energy-width": `${Math.max(6, Math.min(100, Number(device.watts || 0)))}%` }} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};